import type { PreOrderMapValue, SalesMapValue } from "~/hooks/useCollectionTableData"
import type { VariantMatch } from "~/hooks/useVariantMatches"
import { calculateWeightedAverage } from "~/utils/math"

type InputMaps = {
  salesMap?: Map<string, SalesMapValue>
  forecastMap?: Map<string, number>
  preOrderMap?: Map<string, PreOrderMapValue>
  ordersMap?: Map<string, number>
}

type ContributingMatchMinimal = {
  match: string
  similarity: number
}

type Contributor = ContributingMatchMinimal & {
  sales: number
  forecast: number
  preOrderValue: number
  orders: number
}

const emptyPreOrderValue: PreOrderMapValue = {
  value: 0,
  preorders: 0,
  inboundPreorderAmount: 0,
  futurePreorders: 0,
  preOrderAmount: 0,
}

/** Wraps a comparison getter so match-imputed variants read 0. An imputed value copies what the
 *  matched comparison variants sold, so summing it next to those variants double counts. */
export const excludeImputedComparison =
  (matchedVariantsSet: Set<string> | undefined, getValue: (variantId: string) => number) =>
  (variantId: string) => (matchedVariantsSet?.has(variantId) ? 0 : getValue(variantId))

function withValueAndAmount(
  preOrder: PreOrderMapValue | undefined,
  value: number,
  preOrderAmount: number,
): PreOrderMapValue {
  return { ...(preOrder ?? emptyPreOrderValue), value, preOrderAmount }
}

export function resolveComparisonVariantMatches({
  variantIds,
  variantMatchesMap,
  input,
}: {
  variantIds: string[]
  variantMatchesMap?: Map<string, VariantMatch[]>
  input: InputMaps
}): {
  resolvedSalesMap: Map<string, SalesMapValue>
  resolvedForecastMap: Map<string, number>
  resolvedPreOrderMap: Map<string, PreOrderMapValue>
  resolvedOrdersMap: Map<string, number>
  matchedVariantsSet: Set<string>
  directVariantsSet: Set<string>
  contributingMatchesMap: Map<string, ContributingMatchMinimal[]>
  allMatchesMap: Map<string, VariantMatch[]>
} {
  const { salesMap, forecastMap, preOrderMap, ordersMap } = input
  const matchedVariantsSet = new Set<string>()
  const directVariantsSet = new Set<string>()
  const contributingMatchesMap = new Map<string, ContributingMatchMinimal[]>()
  const allMatchesMap = new Map<string, VariantMatch[]>()

  for (const variantId of variantIds) {
    if (
      salesMap?.has(variantId) ||
      forecastMap?.has(variantId) ||
      preOrderMap?.has(variantId) ||
      ordersMap?.has(variantId)
    ) {
      directVariantsSet.add(variantId)
    }
  }

  const resolvedSalesMap = new Map(salesMap ?? [])
  const resolvedForecastMap = new Map(forecastMap ?? [])
  const resolvedPreOrderMap = new Map(preOrderMap ?? [])
  const resolvedOrdersMap = new Map(ordersMap ?? [])

  if (!variantMatchesMap || variantMatchesMap.size === 0) {
    return {
      resolvedSalesMap,
      resolvedForecastMap,
      resolvedPreOrderMap,
      resolvedOrdersMap,
      matchedVariantsSet,
      directVariantsSet,
      contributingMatchesMap,
      allMatchesMap,
    }
  }

  for (const [variantId, matches] of variantMatchesMap) {
    if (directVariantsSet.has(variantId)) continue

    const contributors: Contributor[] = matches.flatMap(({ match, combinedSimilarity }) => {
      const sales = salesMap?.get(match)?.sales ?? 0
      const forecast = forecastMap?.get(match) ?? 0
      const preOrderValue = preOrderMap?.get(match)?.value ?? 0
      const orders = ordersMap?.get(match) ?? 0
      if (sales === 0 && forecast === 0 && preOrderValue === 0 && orders === 0) return []
      return [
        {
          match,
          similarity: combinedSimilarity ?? 1,
          sales,
          forecast,
          preOrderValue,
          orders,
        },
      ]
    })

    if (contributors.length === 0) continue

    const sales = Math.round(calculateWeightedAverage(contributors, "sales", "similarity") ?? 0)
    const forecast = Math.round(
      calculateWeightedAverage(contributors, "forecast", "similarity") ?? 0,
    )
    const preOrderValue = Math.round(
      calculateWeightedAverage(contributors, "preOrderValue", "similarity") ?? 0,
    )
    const orders = Math.round(calculateWeightedAverage(contributors, "orders", "similarity") ?? 0)

    if (sales > 0) {
      const salesAmount =
        calculateWeightedAverage(
          contributors.map((c) => ({
            ...c,
            salesAmount: salesMap?.get(c.match)?.salesAmount ?? 0,
          })),
          "salesAmount",
          "similarity",
        ) ?? 0
      resolvedSalesMap.set(variantId, { sales, salesAmount })
    }
    if (forecast > 0) resolvedForecastMap.set(variantId, forecast)
    if (preOrderValue > 0) {
      const preOrderAmount =
        calculateWeightedAverage(
          contributors.map((c) => ({
            ...c,
            preOrderAmount: preOrderMap?.get(c.match)?.preOrderAmount ?? 0,
          })),
          "preOrderAmount",
          "similarity",
        ) ?? 0
      resolvedPreOrderMap.set(
        variantId,
        withValueAndAmount(preOrderMap?.get(contributors[0]!.match), preOrderValue, preOrderAmount),
      )
    }
    if (orders > 0) resolvedOrdersMap.set(variantId, orders)

    matchedVariantsSet.add(variantId)
    contributingMatchesMap.set(
      variantId,
      contributors.map(({ match, similarity }) => ({ match, similarity })),
    )
    allMatchesMap.set(variantId, matches)
  }

  return {
    resolvedSalesMap,
    resolvedForecastMap,
    resolvedPreOrderMap,
    resolvedOrdersMap,
    matchedVariantsSet,
    directVariantsSet,
    contributingMatchesMap,
    allMatchesMap,
  }
}
